import { useEffect, useState } from 'react';
import { CircleUser, House, LogOut } from 'lucide-react';
import CompanyLogo from '../../common/CompanyLogo';
import { ModalConfirmation } from '../../popup/modalConfirmation';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { useAppSelector } from '../../../store/hooks';
import { useLazyGetUserInfoQuery } from '../../../store/api/userApi';

interface DesktopNavbarProps {
	logout: () => void;
	token: string;
}

interface ProfilePreviewData {
	firstname?: string;
	lastname?: string;
}

export default function DesktopNavbar({ logout, token }: DesktopNavbarProps) {
	const [profile, setProfile] = useState<ProfilePreviewData>();
	const [displayModalConfirmation, setDisplayModalConfirmation] = useState(false);
	const loggedInUserDetails = useAppSelector((state) => state.loggedInUserDetails);
	const [loadUserInfo] = useLazyGetUserInfoQuery();
	const optionStore = { action: 'leavingFL' };
	const userId =
		'id' in loggedInUserDetails && typeof loggedInUserDetails.id === 'number'
			? loggedInUserDetails.id
			: undefined;
	const userEmail =
		'email' in loggedInUserDetails && typeof loggedInUserDetails.email === 'string'
			? loggedInUserDetails.email
			: '';

	useEffect(() => {
		if (!token || !userId) {
			return;
		}

		loadUserInfo({ userId })
			.unwrap()
			.then((user) => setProfile(user as ProfilePreviewData))
			.catch((error) => console.warn('Unable to load account profile:', error));
	}, [loadUserInfo, token, userId]);

	const navigateToOrigin = () => {
		const originUrl = localStorage.getItem('originURL');
		const target = originUrl
			? `${process.env.REACT_APP_PORTAL_BASE_URL}${originUrl}/overviews`
			: `${process.env.REACT_APP_B2C_BASE_URL}main/dashboard`;

		window.open(target, '_self');
		setDisplayModalConfirmation(false);
		localStorage.removeItem('originURL');
	};

	const fullName = `${profile?.firstname ?? ''} ${profile?.lastname ?? ''}`.trim();

	return (
		<header className="app-desktop-nav desktop-navbar-container fixed inset-x-0 top-0 z-40 h-[60px] min-w-0 items-center overflow-x-hidden bg-primary px-5 text-primary-foreground shadow-sm">
			<button
				aria-label="Return to FutureLab dashboard"
				className="flex h-full items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
				onClick={() => setDisplayModalConfirmation(true)}
				type="button"
			>
				<CompanyLogo />
			</button>
			{token && (
				<nav aria-label="Account navigation" className="ml-auto flex h-full min-w-0 shrink-0 items-center gap-1 border-l border-white/30 pl-4">
					<button
						aria-label="Return to dashboard"
						className="rounded-md p-2 transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
						onClick={() => setDisplayModalConfirmation(true)}
						type="button"
					>
						<House aria-hidden="true" size={22} />
					</button>
					<DropdownMenu>
						<DropdownMenuTrigger
							aria-label="Open account menu"
							className="rounded-md p-2 transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
						>
							<CircleUser aria-hidden="true" size={22} />
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] min-w-0 sm:w-auto sm:min-w-56">
							<DropdownMenuLabel>{fullName || 'FutureLab member'}</DropdownMenuLabel>
							<DropdownMenuLabel className="break-all font-normal normal-case">{userEmail}</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuItem className="min-h-10 text-destructive focus:text-destructive" onClick={logout}>
								<LogOut className="size-4" />
								Logout
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</nav>
			)}
			{displayModalConfirmation && (
				<ModalConfirmation
					callBackProceedMethodProp={navigateToOrigin}
					displayModalConfirmation={displayModalConfirmation}
					optionStore={optionStore}
					setDisplayModalConfirmation={setDisplayModalConfirmation}
				/>
			)}
		</header>
	);
}
